import React, { useState, useEffect } from 'react'
import { Box, List, Typography } from '@mui/material'

import {
  AccountDoc,
  GroupDoc,
  GroupStatus,
  UserSettingsDoc
} from 'types/api/changes'
import { allGroups } from 'services/db/Queries/GroupQueries'

import * as Styled from 'components/cards/styled'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { theme } from 'theme'
import { GroupPrivacy } from 'model/models/Group'
import {
  GroupChannelIcon,
  MsgTitle,
  MsgType,
  SelectedCalIcon
} from 'assets/icons'
import { defaultGroupImage, TierFeatures } from 'utils/api/groups'
import GroupTitle from 'components/popups/groupInfo/GroupTitle'
import TitleWithIconRow from 'components/cards/groupCard/TitleWithIconRow'
import { t } from 'i18next'
import { isTrulyEmpty } from 'utils/object'
import i18n from 'i18n'
import ProBadge from 'components/badges/ProBadge'
import { highestActiveTier, paddlePlanToTier, Tier } from 'utils/api/accounts'
import ConfirmationDialog from '../ConfirmationDialog'
import { usePayment } from 'components/providers/PaymentProvider'
import { PlanName } from '../payment/PlansInfoDialog'
import Logger from 'services/Logger'
import { tierToPlan } from 'components/tier/TierHover'
import { tierFeaturesToAnalyticsFeature } from 'components/tier/OutOfTierDialog'
import { isGroupArchived } from 'utils/api/userSettings'
import { isConvertedGroup } from 'utils/groups'
import { Model } from 'types/model'

interface GroupsPickerProps {
  userSettings: UserSettingsDoc
  account: AccountDoc
  selectedGroups: GroupDoc[]
  onGroupToggle: (groupId: GroupDoc) => void
  openedFromGroup: GroupDoc
}

export default function GroupsPicker(props: GroupsPickerProps) {
  const [groups, setGroups] = useState<GroupDoc[]>([])
  const [selectedGroup, setSelectedGroup] = useState<GroupDoc | null>(null)

  useEffect(() => {
    allGroups().then((groups) => {
      const filteredGroups = groups.filter(
        (group) =>
          isGroupArchived(props.userSettings, group._id) === false &&
          isConvertedGroup(group) === false
      )

      // put openedFromGroup at the top of the list
      if (props.openedFromGroup) {
        const openedFromGroupIndex = filteredGroups.findIndex(
          (group) => group._id === props.openedFromGroup._id
        )
        if (openedFromGroupIndex > -1) {
          filteredGroups.splice(openedFromGroupIndex, 1)
          filteredGroups.unshift(props.openedFromGroup as Model<GroupDoc>)
        }
      }

      setGroups(filteredGroups)
    })
  }, [])

  const { showPaymentDialog } = usePayment()

  function toggleGroup(group: GroupDoc) {
    props.onGroupToggle(group)
    setSelectedGroup(null)
  }

  function handleClose(
    status: 'PURCHASED' | 'PURCHASE_ABORTED' | 'DIALOG_CLOSED',
    plan: PlanName
  ) {
    Logger.debug('plan status; plan; =>', status, plan, paddlePlanToTier(plan))

    const shouldHandleAction = paddlePlanToTier(plan) >= Tier.BUSINESS_PLUS

    Logger.debug('selectedGroup', selectedGroup, shouldHandleAction)

    if (shouldHandleAction && selectedGroup) {
      toggleGroup(selectedGroup)
    }
  }

  const openPayments = () => {
    console.log('selectedGroup', selectedGroup)
    showPaymentDialog(tierToPlan(Tier.BUSINESS_PLUS), handleClose, [
      tierFeaturesToAnalyticsFeature(TierFeatures.EMBED_CALENDARS)
    ])
  }

  useEffect(() => {
    if (
      selectedGroup &&
      highestActiveTier(props.account) < Tier.BUSINESS_PLUS
    ) {
      openPayments()
    } else if (selectedGroup) {
      toggleGroup(selectedGroup)
    }
  }, [selectedGroup])

  const handleGroupClick = (group: GroupDoc) => {
    if (isTrulyEmpty(group.PrivateLinkUrl)) return
    if (
      props.selectedGroups.length === 1 &&
      props.selectedGroups.find((g) => g._id === group._id) !== undefined
    )
      return

    setSelectedGroup(group)
  }

  const calendarsTitle = (
    <Typography
      fontWeight={400}
      fontSize={12}
      letterSpacing={'-0.2px'}
      color="rgb(113 113 113);"
    >
      {t('calendarsToShow')}
    </Typography>
  )

  return (
    <Box
      sx={{
        marginTop: '0.5rem',
        paddingTop: 4,
        paddingBottom: 0,
        paddingLeft: 2,
        paddingRight: 2,
        border: '1.5px solid #e4e4e4',
        borderRadius: '5px',
        position: 'relative',
        height: '100%',
        minHeight: '300px',
        marginBottom: 4
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 'auto',
          top: -10,
          left: i18n.dir() === 'ltr' ? 10 : undefined,
          right: i18n.dir() === 'rtl' ? 10 : undefined,
          backgroundColor: theme.palette.background.paper,

          zIndex: 1
        }}
      >
        {highestActiveTier(props.account) < Tier.BUSINESS_PLUS ? (
          <ProBadge neededTier={Tier.BUSINESS_PLUS} title={calendarsTitle} />
        ) : (
          calendarsTitle
        )}
      </Box>
      <List sx={{ height: '100%', overflow: 'auto' }}>
        {groups.map((group) => {
          const groupsSettings = props.userSettings?.GroupsSettings
          const groupColor = groupsSettings?.[group?._id ?? '']?.GroupColor
          const iconSize = 16

          let message = t('tap_select')

          if (
            props.selectedGroups.find((g) => g._id === group._id) !== undefined
          ) {
            message = t('tap_unselect')
          }

          if (isTrulyEmpty(group.PrivateLinkUrl)) {
            message = t('youCannotSelectThisCalendar')
          }

          return (
            <Styled.GroupCardItem
              key={group._id}
              overflow={'hidden'}
              sx={{ height: 'auto' }}
            >
              <Styled.GroupCardButton
                sx={{
                  alignItems: 'start',
                  padding: theme.spacing(2),
                  backgroundColor: isTrulyEmpty(group.PrivateLinkUrl)
                    ? '#e6e6e6'
                    : undefined
                }}
                onClick={handleGroupClick.bind(null, group)}
                className="groupItem"
                selected={
                  props.selectedGroups.find((g) => g._id === group._id) !==
                  undefined
                }
              >
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="auto"
                  height="100%"
                >
                  <Styled.GroupCardAvatarContainer
                    color={groupColor ?? group.GroupColor}
                  >
                    <Styled.GroupCardAvatar
                      asGroup
                      img={group.Photo}
                      name=""
                      emptyPic={
                        group.PrivacyMode === GroupPrivacy.CHANNEL ? (
                          <GroupChannelIcon width={18} height={18} />
                        ) : (
                          defaultGroupImage(group)
                        )
                      }
                    />
                  </Styled.GroupCardAvatarContainer>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                  width={'100%'}
                  marginLeft={i18n.dir() === 'ltr' ? 2 : undefined}
                  marginRight={i18n.dir() === 'rtl' ? 2 : undefined}
                >
                  <Typography
                    fontWeight={500}
                    fontSize={16}
                    width={'100%'}
                    textAlign={'start'}
                  >
                    {group.Name}
                  </Typography>
                  <TitleWithIconRow
                    icon={
                      <MsgTitle
                        style={{
                          minWidth: iconSize
                        }}
                        width={iconSize}
                        height={iconSize}
                      />
                    }
                    title={message}
                  />
                </Box>
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="auto"
                  height="100%"
                >
                  {props.selectedGroups.find((g) => g._id === group._id) !==
                    undefined && <SelectedCalIcon />}
                </Box>
              </Styled.GroupCardButton>
            </Styled.GroupCardItem>
          )
        })}
      </List>
    </Box>
  )
}
